<template>
    <ion-app>
        <div v-if="dbError" class="db-error">
            Storage failed to start on this device — scoring cannot be saved.
            Close the app completely and reopen it. If this keeps happening, contact support.
        </div>
        <router-view/>
        <!-- <ion-router-outlet /> -->
    </ion-app>
</template>

<script>
import sqliteService from "@/services/sqlite-service";
import { useStore } from 'vuex';
import { IonApp, IonRouterOutlet} from '@ionic/vue';
import { defineComponent, onMounted, ref} from "vue";

export default defineComponent({
    name: 'App',
    components: {
        IonApp,
        IonRouterOutlet
    },
    setup() {
        const { databaseStartup, initDbTables } = sqliteService();

        const store = useStore();
        const dbError = ref(false);

        // The launch splash lives in index.html so it paints before any JS.
        // Dismiss it once storage is ready (or has failed — the error banner
        // must become visible), keeping it up at least 900ms so the bounce
        // never just flickers on fast devices.
        const hideSplash = () => {
            const splash = document.getElementById('splash');

            if (!splash) {
                return;
            }

            const wait = Math.max(0, 900 - performance.now());

            setTimeout(() => {
                splash.classList.add('splash-hide');
                setTimeout(() => splash.remove(), 500);
            }, wait);
        };

        onMounted(async () => {
            // Watchdog: some storage failures hang instead of rejecting (e.g.
            // a broken WASM engine aborts inside jeep-sqlite without settling
            // our await). The splash must never be able to hang the UI.
            const splashWatchdog = setTimeout(() => {
                console.error('database startup timed out');
                dbError.value = true;
                hideSplash();
            }, 12000);

            // Web deployment: scores live in browser storage (IndexedDB).
            // Ask the browser to treat it as persistent so it is not evicted
            // under storage pressure.
            try {
                if (navigator.storage && navigator.storage.persist) {
                    await navigator.storage.persist();
                }
            } catch (e) {
                // Not supported — nothing to do.
            }

            try {
                const db = await databaseStartup();

                await initDbTables();

                store.dispatch('db/setDbInstance', db);
            } catch (e) {
                console.error('database issues', e.message);
                dbError.value = true;
            } finally {
                clearTimeout(splashWatchdog);
                hideSplash();
            }
        });

        return {
            dbError
        };
  }
});
</script>

<style>
.db-error {
    background: #eb445a;
    color: #fff;
    font-weight: bold;
    padding: 12px 16px;
    text-align: center;
    position: relative;
    z-index: 9999;
}
</style>